import React, { useState } from "react";
import { Plus, Minus } from "lucide-react";
import { useCurrency } from "@/lib/currency";

/**
 * Inline expandable fare breakdown: Base + Taxes & Fees = Total.
 * Optionally shows applied markup and per-pax math when multiple pax.
 *
 * Props:
 *  - base: number (base fare per pax, in USD)
 *  - taxes: number (taxes & fees per pax, in USD)
 *  - markup: number (already included in `base`, displayed for transparency to admins)
 *  - pax: number (default 1)
 *  - testId: string (root data-testid)
 *  - showMarkup: bool (admin only)
 *  - compact: bool (smaller label)
 *
 * Currency conversion is handled by the global useCurrency() context — all
 * amounts are stored in USD and rendered in the B2C user's chosen display
 * currency with admin's FX margin baked in.
 */
export default function FareBreakdown({
  base = 0,
  taxes = 0,
  markup = 0,
  pax = 1,
  testId = "fare-breakdown",
  showMarkup = false,
  compact = false,
  label = "Fare breakdown",
}) {
  const [open, setOpen] = useState(false);
  const total = Number(base) + Number(taxes);
  const { format: fmt } = useCurrency();

  return (
    <div data-testid={testId} className={compact ? "text-[11px]" : "text-xs"}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        data-testid={`${testId}-toggle`}
        className="inline-flex items-center gap-1 text-[var(--brand-primary)] hover:text-[var(--brand-primary-dark)] font-medium transition-colors"
      >
        {open ? <Minus className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
        {label}
      </button>
      {open && (
        <div data-testid={`${testId}-content`} className="mt-1.5 bg-slate-50 border border-slate-200 rounded-md p-2 space-y-1">
          <div className="flex justify-between gap-3 text-slate-600">
            <span>Base fare</span>
            <span className="font-mono">{fmt(base)}</span>
          </div>
          {showMarkup && markup > 0 && (
            <div className="flex justify-between gap-3 text-slate-400 italic" data-testid={`${testId}-markup`}>
              <span>↳ incl. markup</span>
              <span className="font-mono">+{fmt(markup)}</span>
            </div>
          )}
          <div className="flex justify-between gap-3 text-slate-600">
            <span>Taxes & fees</span>
            <span className="font-mono">{fmt(taxes)}</span>
          </div>
          <div className="border-t border-slate-200 pt-1 flex justify-between gap-3 font-semibold text-[var(--brand-accent)]">
            <span>Per pax total</span>
            <span className="font-mono">{fmt(total)}</span>
          </div>
          {pax > 1 && (
            <div className="flex justify-between gap-3 text-slate-500">
              <span>× {pax} pax</span>
              <span className="font-mono">{fmt(total * pax)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
